import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Typography,
  Box,
  InputAdornment,
  Divider,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VideocamIcon from '@mui/icons-material/Videocam';

function CameraSelectionDialog({
  open,
  onClose,
  onSelect,
  availableCameras,
  selectedCameras,
  loading,
}) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());

  // Group cameras by site
  const camerasBySite = useMemo(() => {
    const groups = {};
    const searchLower = search.toLowerCase();

    availableCameras.forEach((camera) => {
      // Filter by search
      const cameraName = (camera.display_name || camera.name || '').toLowerCase();
      const siteName = (camera.site_display_name || camera.site_name || '').toLowerCase();

      if (search && !cameraName.includes(searchLower) && !siteName.includes(searchLower)) {
        return;
      }

      const siteKey = camera.site_id;
      const siteName2 = camera.site_display_name || camera.site_name || `Site ${camera.site_id}`;

      if (!groups[siteKey]) {
        groups[siteKey] = {
          siteName: siteName2,
          cameras: [],
        };
      }

      groups[siteKey].cameras.push(camera);
    });

    return groups;
  }, [availableCameras, search]);

  // Check if a camera is already in the set
  const isAlreadySelected = (camera) => {
    return selectedCameras.some(
      (c) => c.camera_id === camera.id && c.site_id === camera.site_id
    );
  };

  const handleToggle = (camera) => {
    const key = `${camera.site_id}-${camera.id}`;
    const newSelected = new Set(selected);

    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }

    setSelected(newSelected);
  };

  const handleSelectAll = (cameras) => {
    const newSelected = new Set(selected);
    const allSelected = cameras.every((c) => selected.has(`${c.site_id}-${c.id}`) || isAlreadySelected(c));

    cameras.forEach((camera) => {
      if (isAlreadySelected(camera)) return;
      const key = `${camera.site_id}-${camera.id}`;
      if (allSelected) {
        newSelected.delete(key);
      } else {
        newSelected.add(key);
      }
    });

    setSelected(newSelected);
  };

  const handleConfirm = () => {
    const camerasToAdd = availableCameras.filter((camera) =>
      selected.has(`${camera.site_id}-${camera.id}`)
    );
    onSelect(camerasToAdd);
    setSelected(new Set());
    setSearch('');
    onClose();
  };

  const handleCancel = () => {
    setSelected(new Set());
    setSearch('');
    onClose();
  };

  const siteKeys = Object.keys(camerasBySite);

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Add Cameras</DialogTitle>
      <DialogContent dividers>
        <TextField
          placeholder="Search cameras..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : siteKeys.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <VideocamIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography color="text.secondary">
              {search ? 'No cameras match your search' : 'No cameras available'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {siteKeys.map((siteId, siteIndex) => {
              const group = camerasBySite[siteId];
              const availableInGroup = group.cameras.filter((c) => !isAlreadySelected(c));
              const allGroupSelected = availableInGroup.length > 0 &&
                availableInGroup.every((c) => selected.has(`${c.site_id}-${c.id}`));

              return (
                <Box key={siteId}>
                  {siteIndex > 0 && <Divider sx={{ my: 1 }} />}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 1,
                      py: 0.5,
                      bgcolor: 'action.hover',
                      borderRadius: 1,
                      mb: 0.5,
                    }}
                  >
                    {availableInGroup.length > 0 && (
                      <Checkbox
                        size="small"
                        checked={allGroupSelected}
                        indeterminate={
                          !allGroupSelected &&
                          availableInGroup.some((c) => selected.has(`${c.site_id}-${c.id}`))
                        }
                        onChange={() => handleSelectAll(group.cameras)}
                      />
                    )}
                    <Typography variant="subtitle2" sx={{ flex: 1 }}>
                      {group.siteName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {group.cameras.length} cameras
                    </Typography>
                  </Box>
                  <List dense disablePadding>
                    {group.cameras.map((camera) => {
                      const alreadySelected = isAlreadySelected(camera);
                      const key = `${camera.site_id}-${camera.id}`;
                      const isChecked = selected.has(key);

                      return (
                        <ListItem key={key} disablePadding>
                          <ListItemButton
                            onClick={() => !alreadySelected && handleToggle(camera)}
                            disabled={alreadySelected}
                            dense
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <Checkbox
                                edge="start"
                                checked={isChecked || alreadySelected}
                                disabled={alreadySelected}
                                size="small"
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={camera.display_name || camera.name || `Camera ${camera.id}`}
                              secondary={alreadySelected ? 'Already in set' : null}
                              primaryTypographyProps={{
                                variant: 'body2',
                                color: alreadySelected ? 'text.secondary' : 'text.primary',
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={selected.size === 0}
        >
          Add {selected.size > 0 ? `(${selected.size})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CameraSelectionDialog;
